import { Typography } from '@mui/material'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import IndividualRegistrationForm from './forms/IndividualRegistrationForm'
import MandatoryCheckForm from './forms/MandatoryCheckForm'
import RepeaterForm from './forms/RepeaterForm'
import SizeChartButtons from './forms/utils/SizeChartButtons'
import ImageDialog from './ImageDialog'
import PageTitle from './Page/Title'
import PaymongoCheckout from './PaymongoCheckout'
import { Participant as ParticipantApi } from '../API'
import { EVENT_SHIRT_DETAILS } from '../constants/models'
import { checkPastParticipant } from '../constants/pastParticipants'
import { MandatoryCheckDetailsProps } from '../constants/types'
import { CartContext } from '../contexts/CartContext'
import { PageLoaderContext } from '../contexts/PageLoaderContext'
import { Event } from '../models/API'
// import ShoppingCart from './ShoppingCart'
const STEP_REPEATER = 0
const STEP_MANDATORY_CHECK = 1
const STEP_PARTICIPANT = 2
const STEP_CHECKOUT = 3

const steps = [
  { key: STEP_PARTICIPANT, value: 'Participant details' },
  { key: STEP_CHECKOUT, value: 'Checkout' },
]

type Participant = Omit<Exclude<ParticipantApi, null>, '__typename'>

const SnackbarComponent = ({
  title,
  message,
}: {
  title: string
  message: string
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body1">{title}</Typography>
      <Typography variant="body2">{message}</Typography>
    </div>
  )
}

interface Repeater {
  show: boolean
  slug: string | null
}

interface Props {
  event: Event
  categoryId: string
  repeater: Repeater
  mandatoryCheckDetails?: MandatoryCheckDetailsProps | null
}

const IndividualRegistration: React.FC<Props> = ({
  event,
  categoryId,
  repeater,
  mandatoryCheckDetails,
}) => {
  const navigate = useNavigate()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const displayError = ({
    title,
    message,
  }: {
    title: string
    message: string
  }) => {
    enqueueSnackbar(<SnackbarComponent title={title} message={message} />, {
      variant: 'error',
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
      autoHideDuration: 10000,
      onClose: (event, reason, key) => {
        if (reason === 'instructed') {
          closeSnackbar(key)
        }
      },
    })
  }
  const hasMandatoryCheck = Object.keys(mandatoryCheckDetails || {}).length > 0
  const [activeStep, setActiveStep] = useState(
    repeater.show
      ? STEP_REPEATER
      : hasMandatoryCheck
      ? STEP_MANDATORY_CHECK
      : STEP_PARTICIPANT,
  )

  const handleNext = useCallback(() => {
    setActiveStep((prevActiveStep) => {
      let next = prevActiveStep + 1

      if (next === STEP_MANDATORY_CHECK && !hasMandatoryCheck)
        next = STEP_PARTICIPANT
      return next
    })
  }, [hasMandatoryCheck])

  const handleBack = useCallback(() => {
    setActiveStep((prevActiveStep) => {
      let prev = prevActiveStep - 1
      if (prev === STEP_MANDATORY_CHECK && !hasMandatoryCheck)
        prev = STEP_REPEATER

      if (prev === STEP_REPEATER && !repeater.show) prev = STEP_PARTICIPANT

      return prev
    })
  }, [repeater, hasMandatoryCheck])

  const {
    startedAt,
    cartUser,
    addUser,
    addProduct,
    updateProduct,
    cartItems,
    clearCart,
  } = useContext(CartContext)
  const { setLoading: setPageLoading } = useContext(PageLoaderContext)

  const [categories, setCategories] = useState([])
  const [participant, setParticipant] = useState<Participant>()

  const [showSize, setShowSize] = useState(false)
  const [showBikeSize, setShowBikeSize] = useState(false)
  const [showSizeImage, setShowSizeImage] = useState('')

  const processStartedAt = useCallback(() => {
    clearCart()
  }, [clearCart])

  useEffect(() => {
    if (
      (startedAt === null && Object.keys(cartUser).length > 0) ||
      (startedAt !== null && dayjs().diff(dayjs(startedAt), 'm', true) >= 15)
    )
      processStartedAt()
  }, [startedAt, cartUser, processStartedAt])

  useEffect(() => {
    if (event?.categories?.items) {
      setCategories(
        event?.categories?.items?.map((cat) => ({
          id: cat.id,
          name: cat.name,
          fee: cat.fee,
          adminFee: cat.adminFee,
          priority: cat.priority,
          disabled:
            typeof cat.maxParticipants === 'number' &&
            cat.categoryCount['count'] >= cat.maxParticipants,
          isTandem: cat.isTandem || false,
          tandemAgeRange: `${cat.tandemAgeRange}`,
          eventEndDateTime: event.endDateTime,
          isTriathlon: cat.isTriathlon || false,
          bonusParticipants: cat.bonusParticipants || 0,
          repeaterSlug: cat.repeaterSlug,
          repeaterUntil: cat.repeaterUntil,
          hasJersey: cat.hasJersey || false,
          hasShirt2: cat.hasShirt2 || false,
          mandatoryCheckDetails: cat.mandatoryCheckDetails,
        })),
      )
    }
  }, [event])

  const updateParticipantList = useCallback(() => {
    if (cartItems.length > 0) setParticipant(cartItems[0])
  }, [cartItems])

  useEffect(() => {
    updateParticipantList()
  }, [cartItems, updateParticipantList])

  const repeaterContent = () => {
    return (
      <RepeaterForm
        user={{
          ...cartUser,
        }}
        editing={!!cartUser}
        setLoading={setPageLoading}
        displayError={displayError}
        updateData={(data) => {
          const validRepeater = checkPastParticipant(
            repeater.slug,
            data.bibNumber,
            data.firstName,
            data.lastName,
          )
          if (validRepeater) {
            addUser({
              firstName: data.firstName.trim(),
              lastName: data.lastName.trim(),
            })

            handleNext()
          } else
            return [
              null,
              {
                title:
                  'Sorry. Only repeater (finisher) participants allowed at this stage.',
                message:
                  'Please ensure you enter the correct information, or contact support@megatechph.com for assistance',
              },
            ]
        }}
        onCancel={() => {
          clearCart()
          navigate(-1)
        }}
      />
    )
  }

  const mandatoryCheckContent = useCallback(() => {
    return (
      <MandatoryCheckForm
        user={{
          ...cartUser,
          mandatoryCheck: '',
          ...(participant && {
            mandatoryCheck: participant.mandatoryCheck ?? '',
          }),
        }}
        mandatoryCheckDetails={mandatoryCheckDetails}
        editing={!!cartUser}
        setLoading={setPageLoading}
        displayError={displayError}
        updateData={(data) => {
          const product = {
            ...participant,
            mandatoryCheck: data.mandatoryCheck,
          }

          if (cartItems.length > 0) {
            updateProduct(product)
          } else {
            addProduct({
              ...product,
              id: uuidv4(),
            })
          }

          setPageLoading(false)
          handleNext()
        }}
        onCancel={() => {
          clearCart()
          navigate(-1)
        }}
      />
    )
  }, [cartUser, participant, mandatoryCheckDetails])

  const userDetailsContent = useCallback(() => {
    return (
      <>
        <SizeChartButtons
          shirtDetails={event.shirtDetails}
          categoryId={categoryId}
          categories={categories}
          jerseySizeChart={event.jerseySizeChart}
          onShowSize={(url) => {
            setShowSizeImage(url)
            setShowSize(true)
            setShowBikeSize(false)
          }}
        />
        <div style={{ height: 30 }} />

        <IndividualRegistrationForm
          user={{
            categoryId,
            nationality: 'PH',
            ...cartUser,
            ...(participant && {
              shirtSize: participant.shirtSize,
            }),
            hasJersey: categories.filter((cat) => cat.id === categoryId)[0]
              ?.hasJersey,
            hasShirt2: categories.filter((cat) => cat.id === categoryId)[0]
              ?.hasShirt2,
            shirtDetails: event.shirtDetails || EVENT_SHIRT_DETAILS,
          }}
          showRepeater={repeater.show}
          hasBackSteps={repeater.show || hasMandatoryCheck}
          editing={!!cartUser}
          setLoading={setPageLoading}
          updateData={(data) => {
            addUser({
              email: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              phoneNumber: data.phoneNumber,
            })

            const fee = categories.filter((cat) => cat.id === categoryId)[0].fee
            const adminFee = categories.filter(
              (cat) => cat.id === categoryId,
            )[0].adminFee

            const product = {
              ...participant,
              firstName: data.firstName,
              lastName: data.lastName,
              shirtSize: data.shirtSize,
              jerseySize: `${data.jerseySize || ''}`,
              ...('shirtType' in data && {
                shirtType: `${data.shirtType || ''}`,
              }),
              ...('shirtSize2' in data && {
                shirtSize2: `${data.shirtSize2 || ''}`,
              }),
              ...('shirtType2' in data && {
                shirtType2: `${data.shirtType2 || ''}`,
              }),
              bibName: `${data.bibName || ''}`,
              teamName: `${data.teamName || ''}`,
              birthdate: `${data.birthdate}`,
              nationality: data.nationality,
              gender: `${data.gender}`,
              address: `${data.address}`,
              phoneNumber: `${data.phoneNumber}`,
              emergencyContactName: data.emergencyContactName,
              emergencyContactPhone: data.emergencyContactPhone,
              eventId: event.id,
              eventName: `${event.isVirtual ? 'VIRTUAL - ' : ''}${event.name}`,
              categoryId,
              categoryName: categories.filter((cat) => cat.id === categoryId)[0]
                ?.name,
              fee,
              adminFee,
            }

            if (cartItems.length > 0) {
              updateProduct(product)
            } else {
              addProduct({
                ...product,
                id: uuidv4(),
              })
            }

            setPageLoading(false)
            handleNext()
          }}
          onCancel={() => {
            if (repeater.show || hasMandatoryCheck) {
              handleBack()
            } else {
              clearCart()
              navigate(-1)
            }
          }}
        />
      </>
    )
  }, [categories, categoryId, cartUser, event, participant])

  const checkoutContent = () => {
    return (
      <PaymongoCheckout
        onCancel={handleBack}
        isVirtualEvent={!!event?.isVirtual}
        eventWaiver={event?.waiver}
        isFree={'isFree' in event && event.isFree === 1}
      />
    )
  }

  // const stepProps: { completed?: boolean } = {}

  return (
    <>
      <PageTitle
        title={`Register to ${
          event?.isVirtual ? 'VIRTUAL - ' : ''
        }${event?.name}`}
      />
      <div style={{ height: 48 }} />
      <Stepper activeStep={activeStep}>
        {repeater.show && (
          <Step
            key={'Repeater'}
            completed={STEP_REPEATER < activeStep}
            active={STEP_REPEATER === activeStep}>
            <StepLabel>Repeater</StepLabel>
          </Step>
        )}
        {hasMandatoryCheck && (
          <Step
            key={'Mandatory Check'}
            completed={STEP_MANDATORY_CHECK < activeStep}
            active={STEP_MANDATORY_CHECK === activeStep}>
            <StepLabel>Before we proceed</StepLabel>
          </Step>
        )}

        {steps.map(({ key, value }) => {
          return (
            <Step
              key={value}
              completed={key < activeStep}
              active={key === activeStep}>
              <StepLabel>{value}</StepLabel>
            </Step>
          )
        })}
      </Stepper>
      <div style={{ height: 32 }} />
      <React.Fragment>
        {repeater.show && activeStep === STEP_REPEATER && repeaterContent()}
        {hasMandatoryCheck &&
          activeStep === STEP_MANDATORY_CHECK &&
          mandatoryCheckContent()}
        {activeStep === STEP_PARTICIPANT && userDetailsContent()}
        {activeStep === STEP_CHECKOUT && checkoutContent()}
        <ImageDialog
          open={showSize || showBikeSize}
          onDialogClose={() => {
            setShowSize(false)
            setShowBikeSize(false)
          }}
          onButtonClose={() => {
            setShowSizeImage('')
            setShowSize(false)
            setShowBikeSize(false)
          }}
          image={showSizeImage}
          local
        />
      </React.Fragment>
    </>
  )
}
export default IndividualRegistration
